/**
 * Two lists, not one.
 *
 * This page was everything in one table — a task somebody has to do, a note
 * somebody wrote, and a line the app writes when a card moves — all with the
 * same checkbox, told apart only by reading them. Seven rows in ten were the
 * app talking to itself, and the one thing the page is for, the to-do list,
 * was buried in them.
 *
 * **To do** is work waiting: a checkbox each, oldest due first. That is the
 * page's job, so it opens there.
 *
 * **History** is what has happened, newest first and read-only — ticking a
 * thing that already happened means nothing. Rows the app wrote carry a quiet
 * tag, because "Moved to Proposal" is a fact about the board and not about
 * the customer, and the difference is the whole reason the trail was
 * misleading.
 */
import { useMemo, useState } from 'react';
import { useApp } from '../store/AppContext.jsx';
import { parseDate, daysTo, formatDate } from '../lib/dates.js';
import Icon from '../lib/icons.jsx';
import { Card, CardHead, Pill, OriginPill, Dash } from '../components/Primitives.jsx';
import { TileRow } from '../components/StatCards.jsx';
import CrmFilter from '../components/CrmFilter.jsx';
import { filterActivities } from '../domain/crm.js';

export default function Activities() {
  const { S, crm, cF, setSheet, saveActivity, removeFromCrm } = useApp();

  const [tab, setTab] = useState('todo');
  /* '' both · 'person' what somebody did · 'system' what the app recorded */
  const [who, setWho] = useState('');

  const all = useMemo(() => filterActivities(crm, cF), [crm, cF]);
  const open = crm.acts.filter(a => !a.done);

  const todo = all.filter(a => !a.done);
  const history = all
    .filter(a => a.done)
    .filter(a => !who || (who === 'system' ? a.by === 'system' : a.by !== 'system'));

  const list = tab === 'todo' ? todo : history;

  const byPerson = all.filter(a => a.done && a.by !== 'system').length;
  const bySystem = all.filter(a => a.done && a.by === 'system').length;

  const remove = (e, id) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    if (window.confirm('Delete this activity?')) removeFromCrm('acts', id);
  };

  return (
    <>
      <TileRow
        tiles={[
          ['Open', open.length, 'clock', 'g-pink'],
          ['Overdue', open.filter(a => parseDate(a.due) && daysTo(parseDate(a.due)) < 0).length, 'alert', 'g-red'],
          ['Today', open.filter(a => parseDate(a.due) && daysTo(parseDate(a.due)) === 0).length, 'cal', 'g-amber'],
          ['Done', crm.acts.filter(a => a.done).length, 'check', 'g-green']
        ]}
      />

      {/* The search box is shared with the other CRM pages, so a query typed
          on the pipeline still applies here. Without this bar it would filter
          the list to nothing with no way to see why. */}
      <CrmFilter kind="activity" />

      <Card>
        <CardHead
          icon="cal"
          tone="g-pink"
          title={tab === 'todo' ? 'To do' : 'History'}
          count={list.length}
        >
          <div className="acttabs" role="tablist" aria-label="Activities">
            {[['todo', 'To do', todo.length, 'clock'],
              ['history', 'History', history.length, 'check']].map(([k, label, n, icon]) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={tab === k}
                  className={tab === k ? 'on' : undefined}
                  onClick={() => setTab(k)}
                >
                  <Icon name={icon} />{label}<b>{n}</b>
                </button>
              ))}
          </div>

          {/* Only on History: a to-do list is by definition somebody's work,
              so there is nothing to tell apart there. */}
          {tab === 'history' ? (
            <select
              className="actwho"
              value={who}
              onChange={e => setWho(e.target.value)}
              aria-label="Who recorded it"
            >
              <option value="">Everything ({byPerson + bySystem})</option>
              <option value="person">What people did ({byPerson})</option>
              <option value="system">What the app recorded ({bySystem})</option>
            </select>
          ) : null}

          <button type="button" className="btn sm" onClick={() => setSheet({ kind: 'activity' })}>
            <Icon name="plus" />Log activity
          </button>
        </CardHead>

        <div className="tscroll">
          <table>
            <thead>
              <tr>
                <th />
                <th>{tab === 'todo' ? 'To do' : 'What happened'}</th>
                <th>Type</th><th>Deal</th><th>Source</th><th>Owner</th>
                <th>{tab === 'todo' ? 'Due' : 'Date'}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map(a => {
                const due = parseDate(a.due);
                const late = due && daysTo(due) < 0 && !a.done;
                const deal = crm.deals.find(x => x.id === a.dealId);

                return (
                  <tr key={a.id} onClick={() => setSheet({ kind: 'activity', id: a.id })}>
                    {/* A checkbox on History would be offering to un-happen
                        something. It reads as a record there, and a record is
                        not a control. */}
                    <td style={{ width: 44 }}>
                      {tab === 'todo' ? (
                        <input
                          type="checkbox"
                          className="chk"
                          checked={!!a.done}
                          onClick={e => e.stopPropagation()}
                          onChange={e => saveActivity(a.id, { ...a, done: e.target.checked })}
                        />
                      ) : (
                        <span className={`actby ${a.by === 'system' ? 'sys' : 'person'}`}>
                          <Icon name={a.by === 'system' ? 'gear' : 'users'} />
                        </span>
                      )}
                    </td>
                    <td>
                      <b>{a.subject}</b>
                      {/* Said once, on the row, rather than left to be worked
                          out from the wording. */}
                      {tab === 'history' && a.by === 'system'
                        ? <span className="acttag">recorded by the app</span> : null}
                    </td>
                    <td><Pill tone="p-blue">{a.type}</Pill></td>
                    <td className="dim">{deal ? deal.title : '—'}</td>
                    {/* An activity has no source of its own — it inherits the
                        one from the deal it was logged against. */}
                    <td>{deal ? <OriginPill value={deal.origin} /> : <Dash />}</td>
                    <td>{a.who || '—'}</td>
                    <td>
                      {due
                        ? <span className={`datep${late ? ' bad' : ''}`}><Icon name="cal" />{formatDate(due, false, S.dateFmt)}</span>
                        : <Dash />}
                    </td>
                    <td>
                      <div className="acts">
                        <button
                          type="button"
                          title="Edit"
                          onClick={e => { e.stopPropagation(); setSheet({ kind: 'activity', id: a.id }); }}
                        >
                          <Icon name="edit" />
                        </button>
                        <button type="button" className="rm" title="Delete" onClick={e => remove(e, a.id)}>
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
